import classNames from 'classnames';
import { CarouselController } from '../../../components/Carousel';
import ImageGallery from '../../../components/ImageGallery/ImageGallery';
import React from 'react';
import styles from '../../../views/ProductDetails/Series3CollarDetails/components/ImageGallery/ImageGallery.module.scss';
import { Species } from '../../../types/gql-op-types';
import { IImage, IVideo } from '../../../types';
import { showNanoVariant } from '../../NanoBanner/NanoBanner';
import { getFiGrowthBook } from '../../../lib/growthbook';
import { useLocation } from 'react-router-dom';
import AppPaths from '../../../AppPaths';
import { useIsGift } from '../../../lib/util/isGift';

interface DesktopImageGalleryProps {
  carouselControllerRef?: React.MutableRefObject<CarouselController | undefined>;
  onChange?: (index: number) => void;
  children?: React.ReactNode;
  species: Species;
  /** Dynamic images from API - if provided, overrides hardcoded images */
  dynamicImages?: (IImage | IVideo)[] | null;
}

function getMedia(species: Species, updatedImages = false, isGift: boolean): IImage[] {
  const experiment = getFiGrowthBook().getFeatureValue<boolean>('ecom-mini-updated-carousel', false);

  if (isGift) {
    return [
      {
        type: 'image',
        url: `/product_images/mini/gift/mini-01.jpg`,
        descriptors: ['1500w', '3000w', '4000w'],
      },
    ];
  }

  if (experiment || updatedImages) {
    const limit = species === Species.CAT ? 5 : 7;

    return Array.from({ length: limit }, (_, i) => ({
      type: 'image',
      url: `/product_images/mini/experiment/mini-0${i + 1}-${species.toLowerCase()}.jpg`,
      descriptors: ['1500w', '3000w', '4000w'],
    }));
  }

  return [
    {
      type: 'image',
      url: `/product_images/mini/mini-02-${species.toLowerCase()}.jpg`,
      descriptors: ['1500w', '3000w', '4000w'],
    },
    {
      type: 'image',
      url: `/product_images/mini/mini-02-old.jpg`,
      descriptors: ['1500w', '3000w', '4000w'],
    },
    {
      type: 'image',
      url: `/product_images/mini/mini-03-${species.toLowerCase()}.jpg`,
      descriptors: ['1500w', '3000w', '4000w'],
    },
    {
      type: 'image',
      url: `/product_images/mini/mini-04-${species.toLowerCase()}.jpg`,
      descriptors: ['1500w', '3000w', '4000w'],
    },
    {
      type: 'image',
      url: `/product_images/mini/mini-05-${species.toLowerCase()}.jpg`,
      descriptors: ['1500w', '3000w', '4000w'],
    },
  ];
}

export function DesktopImageGallery({
  carouselControllerRef,
  onChange,
  children,
  species,
  dynamicImages,
}: DesktopImageGalleryProps) {
  const nanoVariantEnabled = showNanoVariant();

  /*
   * While we're still running the experiment for the updated Mini images on the general PDP,
   * we definitely want to use them on the Gift With Purhcase page.
   */
  const { pathname } = useLocation();
  const updatedMiniImages = pathname.startsWith(AppPaths.MiniRedeem);
  const isGift = useIsGift();

  // Use dynamic images if provided, otherwise fall back to hardcoded
  const media = dynamicImages ?? getMedia(species, updatedMiniImages, isGift);

  return (
    <div className={classNames(styles.imageGalleryContainer, styles.desktopImageGallery)} style={{ borderRadius: 0 }}>
      {children}
      <ImageGallery
        parentCarouselControllerRef={carouselControllerRef}
        media={media}
        pageDots={true}
        disableAnimation={true}
        friction={0.4}
        onChange={onChange}
        defaultImageIndex={nanoVariantEnabled ? 1 : undefined}
        noBorderRadius={true}
        // isGift uses FiImage, which requires square aspect ratio. All other Mini images don't need this.
        squareAspectRatio={isGift}
      />
    </div>
  );
}
